import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  FileText, UserPlus, Search, Clock, CheckCircle, AlertTriangle,
  ArrowRight, RefreshCw, ChevronLeft, Calendar, User, Phone,
  Building, Activity, Heart, Stethoscope, ClipboardList, Zap
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  urgent: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  normal: "bg-blue-100 text-blue-800 border-blue-200",
  low: "bg-gray-100 text-gray-800 border-gray-200"
};

const STATUS_CONFIG = {
  pending: { label: "Pending Review", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  under_review: { label: "Under Review", color: "bg-blue-100 text-blue-800", icon: Activity },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-800", icon: CheckCircle },
  admitted: { label: "Admitted to EMR", color: "bg-teal-100 text-teal-800", icon: Heart },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: AlertTriangle }
};

const LEVEL_OF_CARE_OPTIONS = [
  { value: "routine", label: "Routine Home Care" },
  { value: "continuous", label: "Continuous Care" },
  { value: "inpatient", label: "General Inpatient" },
  { value: "respite", label: "Respite Care" }
];

export default function ReferralToEMR() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [activationDialog, setActivationDialog] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activationProgress, setActivationProgress] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    ready_for_emr: 0
  });

  // Activation form state
  const [activationForm, setActivationForm] = useState({
    admission_date: new Date().toISOString().split("T")[0],
    primary_diagnosis: "",
    primary_diagnosis_icd10: "",
    attending_physician: "",
    attending_physician_npi: "",
    level_of_care: "routine",
    create_initial_care_plan: true,
    create_initial_medications: true
  });

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("hospice_token");
      
      // Fetch pending referrals for EMR conversion
      const response = await fetch(`${API_URL}/api/ehr/referral-to-emr/pending`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals || []);
        
        // Calculate stats
        const total = data.referrals?.length || 0;
        const accepted = data.referrals?.filter(r => r.status === "accepted").length || 0;
        const pending = data.referrals?.filter(r => r.status === "under_review").length || 0;
        const readyForEMR = data.referrals?.filter(r => !r.already_converted && r.status === "accepted").length || 0;
        
        setStats({ total, pending, accepted, ready_for_emr: readyForEMR });
      }
    } catch (error) {
      console.error("Error fetching referrals:", error);
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const openActivationDialog = (referral) => {
    setSelectedReferral(referral);
    setActivationForm({
      admission_date: new Date().toISOString().split("T")[0],
      primary_diagnosis: referral.patient?.primary_diagnosis || "",
      primary_diagnosis_icd10: "",
      attending_physician: referral.patient?.physician_name || "",
      attending_physician_npi: "",
      level_of_care: "routine",
      create_initial_care_plan: true,
      create_initial_medications: true
    });
    setActivationDialog(true);
  };

  const handleActivateToEMR = async () => {
    if (!selectedReferral) return;

    try {
      setActivating(true);
      setActivationProgress(0);

      // Simulate progress stages
      const progressInterval = setInterval(() => {
        setActivationProgress(prev => {
          if (prev >= 90) return prev;
          return prev + 15;
        });
      }, 300);

      const token = localStorage.getItem("hospice_token");
      const userId = localStorage.getItem("userId") || "system";

      const response = await fetch(`${API_URL}/api/ehr/referral-to-emr`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          referral_id: selectedReferral.id,
          admission_date: new Date(activationForm.admission_date).toISOString(),
          primary_diagnosis: activationForm.primary_diagnosis,
          primary_diagnosis_icd10: activationForm.primary_diagnosis_icd10,
          attending_physician: activationForm.attending_physician,
          attending_physician_npi: activationForm.attending_physician_npi,
          level_of_care: activationForm.level_of_care,
          create_initial_care_plan: activationForm.create_initial_care_plan,
          create_initial_medications: activationForm.create_initial_medications,
          activated_by: userId
        })
      });

      clearInterval(progressInterval);
      setActivationProgress(100);

      if (response.ok) {
        const result = await response.json();
        toast.success(
          <div>
            <strong>Patient Activated to EMR!</strong>
            <br />
            <span className="text-sm">MRN: {result.mrn}</span>
          </div>
        );
        setActivationDialog(false);
        setSelectedReferral(null);
        fetchReferrals();

        // Offer to navigate to the new chart
        if (result.chart_id) {
          setTimeout(() => {
            if (window.confirm(`Patient chart created (MRN: ${result.mrn}). Open the chart now?`)) {
              navigate(`/hospice-ehr`);
            }
          }, 500);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to activate referral");
      }
    } catch (error) {
      console.error("Activation error:", error);
      toast.error("Failed to activate referral to EMR");
    } finally {
      setActivating(false);
      setActivationProgress(0);
    }
  };

  const filteredReferrals = referrals.filter(ref => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ref.patient?.name?.toLowerCase().includes(search) ||
      ref.source_organization?.toLowerCase().includes(search) ||
      ref.id?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-700 via-teal-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/20"
                onClick={() => navigate("/hospice-ehr")}
                data-testid="back-to-ehr-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" /> Back to EHR
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  <Zap className="w-7 h-7" />
                  Activate Referral to EMR
                </h1>
                <p className="text-cyan-100 mt-1">Convert accepted referrals into patient charts</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={fetchReferrals}
              data-testid="refresh-referrals-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <FileText className="w-8 h-8 text-slate-600" />
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Referrals</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
                <p className="text-xs text-yellow-600">Under Review</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-800">{stats.accepted}</p>
                <p className="text-xs text-green-600">Accepted</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg">
              <Zap className="w-8 h-8 text-cyan-600" />
              <div>
                <p className="text-2xl font-bold text-cyan-800">{stats.ready_for_emr}</p>
                <p className="text-xs text-cyan-600">Ready for EMR</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Search */}
        <Card className="mb-6 shadow-sm">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by patient name, source organization, or referral ID..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-referrals-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referrals List */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-slate-50/50">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Referrals Ready for Activation
            </CardTitle>
            <CardDescription>
              Convert accepted referrals into full patient EMR charts
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-cyan-600" />
                <span className="ml-2 text-gray-600">Loading referrals...</span>
              </div>
            ) : filteredReferrals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No referrals ready for activation</p>
                <p className="text-sm mt-1">Referrals must be accepted before they can be converted to EMR</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredReferrals.map(referral => {
                  const status = STATUS_CONFIG[referral.status] || STATUS_CONFIG.pending;
                  const StatusIcon = status.icon;
                  const isConverted = referral.already_converted;
                  const canActivate = referral.status === "accepted" && !isConverted;

                  return (
                    <div
                      key={referral.id}
                      className={`p-5 hover:bg-slate-50/80 transition-colors ${
                        isConverted ? "opacity-60" : ""
                      }`}
                      data-testid={`referral-row-${referral.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`p-3 rounded-lg ${isConverted ? "bg-teal-100" : "bg-slate-100"}`}>
                            <User className={`w-6 h-6 ${isConverted ? "text-teal-600" : "text-slate-600"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <h4 className="font-semibold text-gray-900 text-lg">
                                {referral.patient?.name || "Unknown Patient"}
                              </h4>
                              <Badge className={status.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </Badge>
                              <Badge className={PRIORITY_COLORS[referral.priority] || PRIORITY_COLORS.normal}>
                                {referral.priority?.toUpperCase()}
                              </Badge>
                              {isConverted && (
                                <Badge className="bg-teal-100 text-teal-800">
                                  MRN: {referral.mrn}
                                </Badge>
                              )}
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <Stethoscope className="w-4 h-4 text-gray-400" />
                                <span>{referral.patient?.primary_diagnosis || "No diagnosis"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Building className="w-4 h-4 text-gray-400" />
                                <span>{referral.source_organization || referral.source || "Unknown source"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <span>{referral.patient?.phone || "No phone"}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span>Referred: {formatDate(referral.created_at)}</span>
                              </div>
                            </div>

                            {referral.reason_for_referral && (
                              <p className="mt-2 text-sm text-gray-500 line-clamp-2">
                                {referral.reason_for_referral}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          {canActivate ? (
                            <Button
                              className="bg-cyan-600 hover:bg-cyan-700 text-white"
                              onClick={() => openActivationDialog(referral)}
                              data-testid={`activate-btn-${referral.id}`}
                            >
                              <Zap className="w-4 h-4 mr-2" />
                              Activate to EMR
                            </Button>
                          ) : isConverted ? (
                            <Button
                              variant="outline"
                              onClick={() => navigate("/hospice-ehr")}
                              data-testid={`view-chart-btn-${referral.id}`}
                            >
                              <ArrowRight className="w-4 h-4 mr-2" />
                              View Chart
                            </Button>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">
                              Awaiting Acceptance
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activation Dialog */}
      <Dialog open={activationDialog} onOpenChange={setActivationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-600" />
              Activate Referral to EMR
            </DialogTitle>
            <DialogDescription>
              Convert {selectedReferral?.patient?.name}'s referral into a full patient chart
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Patient Summary */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Patient Information</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <span className="ml-2 font-medium">{selectedReferral?.patient?.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">DOB:</span>
                  <span className="ml-2 font-medium">{selectedReferral?.patient?.date_of_birth || "Not provided"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Phone:</span>
                  <span className="ml-2 font-medium">{selectedReferral?.patient?.phone || "Not provided"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Insurance:</span>
                  <span className="ml-2 font-medium">{selectedReferral?.patient?.insurance_provider || "Not provided"}</span>
                </div>
              </div>
            </div>

            {/* Activation Form */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Admission Date *</Label>
                <Input
                  type="date"
                  value={activationForm.admission_date}
                  onChange={(e) => setActivationForm({...activationForm, admission_date: e.target.value})}
                  data-testid="admission-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Level of Care *</Label>
                <Select
                  value={activationForm.level_of_care}
                  onValueChange={(value) => setActivationForm({...activationForm, level_of_care: value})}
                >
                  <SelectTrigger data-testid="level-of-care-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVEL_OF_CARE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Diagnosis</Label>
                <Input
                  placeholder="e.g., CHF, COPD, Cancer..."
                  value={activationForm.primary_diagnosis}
                  onChange={(e) => setActivationForm({...activationForm, primary_diagnosis: e.target.value})}
                  data-testid="primary-diagnosis-input"
                />
              </div>
              <div className="space-y-2">
                <Label>ICD-10 Code</Label>
                <Input
                  placeholder="e.g., I50.9"
                  value={activationForm.primary_diagnosis_icd10}
                  onChange={(e) => setActivationForm({...activationForm, primary_diagnosis_icd10: e.target.value})}
                  data-testid="icd10-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Attending Physician</Label>
                <Input
                  placeholder="Physician name"
                  value={activationForm.attending_physician}
                  onChange={(e) => setActivationForm({...activationForm, attending_physician: e.target.value})}
                  data-testid="physician-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Physician NPI</Label>
                <Input
                  placeholder="10-digit NPI"
                  value={activationForm.attending_physician_npi}
                  onChange={(e) => setActivationForm({...activationForm, attending_physician_npi: e.target.value})}
                  data-testid="npi-input"
                />
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t">
              <Label className="text-sm font-medium">Initial Setup Options</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="create-care-plan"
                  checked={activationForm.create_initial_care_plan}
                  onCheckedChange={(checked) => setActivationForm({...activationForm, create_initial_care_plan: checked})}
                  data-testid="create-care-plan-checkbox"
                />
                <label htmlFor="create-care-plan" className="text-sm">
                  Create initial care plan with standard interventions
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="create-meds"
                  checked={activationForm.create_initial_medications}
                  onCheckedChange={(checked) => setActivationForm({...activationForm, create_initial_medications: checked})}
                  data-testid="create-meds-checkbox"
                />
                <label htmlFor="create-meds" className="text-sm">
                  Initialize medication list from referral
                </label>
              </div>
            </div>

            {activating && (
              <div className="space-y-2 pt-4">
                <Progress value={activationProgress} className="h-2" />
                <p className="text-sm text-center text-gray-600">
                  {activationProgress < 30 && "Creating patient chart..."}
                  {activationProgress >= 30 && activationProgress < 60 && "Initializing HOPE timeline..."}
                  {activationProgress >= 60 && activationProgress < 90 && "Setting up care plan..."}
                  {activationProgress >= 90 && "Finalizing activation..."}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActivationDialog(false)} disabled={activating}>
              Cancel
            </Button>
            <Button
              onClick={handleActivateToEMR}
              disabled={activating}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="confirm-activate-btn"
            >
              {activating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Activate to EMR
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
